const DB_NAME = "boonline-offline";
const DB_VERSION = 3;
const STORE_NAME = "drafts";
const CATALOG_STORE = "catalogs";

export const offlineDraftsEnabled =
  process.env.REACT_APP_ENABLE_OFFLINE_DRAFTS === "true";

function openDatabase() {
  if (!window.indexedDB) {
    return Promise.reject(new Error("Este navegador não oferece IndexedDB."));
  }
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: "localId" });
        store.createIndex("userId", "userId", { unique: false });
        store.createIndex("updatedAt", "updatedAt", { unique: false });
      }
      if (!database.objectStoreNames.contains(CATALOG_STORE)) {
        database.createObjectStore(CATALOG_STORE, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("Não foi possível abrir o armazenamento local."));
    request.onblocked = () => reject(new Error("Feche outras abas do BO Online para atualizar o armazenamento local."));
  });
}

async function transact(mode, operation) {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);
    let result;
    try {
      result = operation(store);
    } catch (error) {
      database.close();
      reject(error);
      return;
    }
    transaction.oncomplete = () => {
      database.close();
      resolve(result && result.result);
    };
    transaction.onerror = () => {
      database.close();
      reject(transaction.error || new Error("Falha ao gravar o rascunho neste dispositivo."));
    };
    transaction.onabort = () => {
      database.close();
      reject(transaction.error || new Error("A gravação local foi cancelada."));
    };
  });
}

export function putDraft(record) {
  return transact("readwrite", (store) => store.put(record));
}

export async function putDraftPreservingInFlight(record) {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(record.localId);
    request.onsuccess = () => {
      const previous = request.result;
      if (previous?.discarded) return;
      store.put({ ...record, ...(previous?.inFlightMutation ? { inFlightMutation: previous.inFlightMutation } : {}) });
    };
    request.onerror = () => transaction.abort();
    transaction.oncomplete = () => { database.close(); resolve(true); };
    transaction.onerror = () => { database.close(); reject(transaction.error || new Error("Falha ao atualizar o rascunho.")); };
    transaction.onabort = () => { database.close(); reject(transaction.error || new Error("A atualização do rascunho foi cancelada.")); };
  });
}

export async function markDraftInFlight(localId, mutation) {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(localId);
    let found = false;
    request.onsuccess = () => {
      if (!request.result || request.result.discarded) return;
      found = true;
      store.put({ ...request.result, status: "syncing", inFlightMutation: mutation });
    };
    request.onerror = () => transaction.abort();
    transaction.oncomplete = () => { database.close(); resolve(found); };
    transaction.onerror = () => { database.close(); reject(transaction.error || new Error("Falha ao registrar a sincronização pendente.")); };
    transaction.onabort = () => { database.close(); reject(transaction.error || new Error("O registro da sincronização foi cancelado.")); };
  });
}

export function getDraft(localId) {
  return transact("readonly", (store) => store.get(localId));
}

export function deleteDraft(localId) {
  // Retain only a tombstone: delayed writers in this or another tab cannot
  // recreate the discarded payload or put it back in the synchronization queue.
  return transact("readwrite", (store) => store.put({ localId, discarded: true, status: "discarded" }));
}

function transactCatalog(mode, operation) {
  return openDatabase().then((database) => new Promise((resolve, reject) => {
    const transaction = database.transaction(CATALOG_STORE, mode);
    const request = operation(transaction.objectStore(CATALOG_STORE));
    transaction.oncomplete = () => {
      database.close();
      resolve(request && request.result);
    };
    transaction.onerror = () => {
      database.close();
      reject(transaction.error || new Error("Falha no cache local do catálogo."));
    };
  }));
}

export function putNatureCatalog(items) {
  return transactCatalog("readwrite", (store) => store.put({ key: "naturezas", items, updatedAt: new Date().toISOString() }));
}

export async function getNatureCatalog() {
  const catalog = await transactCatalog("readonly", (store) => store.get("naturezas"));
  return catalog?.items || null;
}

export function putLocalityCatalog(municipio, items) {
  return openDatabase().then((database) => new Promise((resolve, reject) => {
    const transaction = database.transaction(CATALOG_STORE, "readwrite");
    transaction.objectStore(CATALOG_STORE).put({ key: `localidades:${municipio}`, items, updatedAt: new Date().toISOString() });
    transaction.oncomplete = () => { database.close(); resolve(); };
    transaction.onerror = () => { database.close(); reject(transaction.error || new Error("Não foi possível salvar o catálogo territorial local.")); };
    transaction.onabort = () => { database.close(); reject(transaction.error || new Error("O salvamento do catálogo territorial local foi cancelado.")); };
  }));
}

export async function getLocalityCatalog(municipio) {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(CATALOG_STORE, "readonly");
    const request = transaction.objectStore(CATALOG_STORE).get(`localidades:${municipio}`);
    request.onsuccess = () => resolve(request.result?.items || null);
    request.onerror = () => reject(request.error || new Error("Não foi possível carregar o catálogo territorial local."));
    transaction.oncomplete = () => database.close();
    transaction.onerror = () => { database.close(); reject(transaction.error); };
  });
}

export async function getLatestDraft(userId) {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readonly");
    const request = transaction.objectStore(STORE_NAME).index("userId").getAll(IDBKeyRange.only(userId));
    request.onsuccess = () => resolve((request.result || []).filter(record => !record.discarded).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0] || null);
    request.onerror = () => reject(request.error || new Error("Não foi possível recuperar os rascunhos locais."));
    transaction.oncomplete = () => database.close();
    transaction.onerror = () => {
      database.close();
      reject(transaction.error || new Error("Não foi possível recuperar os rascunhos locais."));
    };
  });
}

export async function getPendingDrafts(userId) {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readonly");
    const request = transaction.objectStore(STORE_NAME).index("userId").getAll(IDBKeyRange.only(userId));
    request.onsuccess = () => resolve((request.result || [])
      .filter((record) => !record.dados?._adminEditing && (record.serverId || record.finalizationRequestedAt) && (record.status === "pending" || record.status === "syncing"))
      .sort((a, b) => a.updatedAt.localeCompare(b.updatedAt)));
    request.onerror = () => reject(request.error || new Error("Não foi possível consultar a fila local."));
    transaction.oncomplete = () => database.close();
    transaction.onerror = () => {
      database.close();
      reject(transaction.error || new Error("Não foi possível consultar a fila local."));
    };
  });
}

export async function updateDraftAfterSync(localId, syncedVersion, serverReport, syncRequestId) {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(localId);
    let next = null;
    request.onsuccess = () => {
      const current = request.result;
      if (!current || current.discarded) return;
      const sameVersion = current.versaoLocal === syncedVersion;
      const mergedReport = {
        ...current.dados,
        _id: serverReport._id,
        clientRequestId: serverReport.clientRequestId || current.dados.clientRequestId,
        documentRevision: serverReport.documentRevision || 0,
        createdAt: serverReport.createdAt,
        updatedAt: serverReport.updatedAt,
      };
      next = {
        ...current,
        serverId: serverReport._id,
        dados: sameVersion ? { ...serverReport, ...(current.dados._adminEditing ? { _adminEditing: true } : {}), formDrafts: current.dados.formDrafts, clientRequestId: mergedReport.clientRequestId } : mergedReport,
        status: sameVersion ? "synced" : "pending",
        ultimaSincronizacao: new Date().toISOString(),
        tentativas: 0,
        erro: null,
        updatedAt: sameVersion ? current.updatedAt : new Date().toISOString(),
        ...(current.inFlightMutation?.syncRequestId === syncRequestId ? { inFlightMutation: null } : {}),
      };
      store.put(next);
    };
    request.onerror = () => transaction.abort();
    transaction.oncomplete = () => {
      database.close();
      resolve(next);
    };
    transaction.onerror = () => {
      database.close();
      reject(transaction.error || new Error("Não foi possível concluir a sincronização local."));
    };
    transaction.onabort = () => {
      database.close();
      reject(transaction.error || new Error("A atualização local da sincronização foi cancelada."));
    };
  });
}

export function migrateLegacyDraft(userId, localIdFactory) {
  const key = `boletim-draft:${userId}`;
  const legacy = window.localStorage.getItem(key);
  if (!legacy) return Promise.resolve(null);
  let report;
  try {
    report = JSON.parse(legacy);
  } catch {
    return Promise.reject(new Error("O rascunho antigo está corrompido; ele foi mantido para recuperação manual."));
  }
  const clientRequestId = report.clientRequestId || localIdFactory();
  const record = {
    localId: report._id ? `server:${report._id}` : clientRequestId,
    serverId: report._id || null,
    userId,
    dados: { ...report, clientRequestId },
    // The legacy localStorage record had no dependable sync marker. Keep it
    // pending even when it contains a server id so local edits are not lost.
    status: report._id ? "pending" : "draft",
    versaoLocal: 1,
    ultimaAlteracao: new Date().toISOString(),
    ultimaSincronizacao: null,
    tentativas: 0,
    erro: null,
    updatedAt: new Date().toISOString(),
  };
  return putDraft(record).then(() => {
    window.localStorage.removeItem(key);
    return record;
  });
}
